import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { apiFetch } from '../api/api';
import { isAppServiceItem, withServiceQuery } from '../config/service';
import ProductGridItem from '../components/ProductGridItem';
import { mapApiProduct, sortApiProducts } from '../utils/mapProduct';

const pagePaperSx = {
  elevation: 0,
  variant: 'outlined',
  sx: {
    borderRadius: 4,
    bgcolor: 'background.paper',
    boxShadow: '0 4px 24px rgba(42,147,143,0.06)',
    borderColor: 'divider',
  },
};

function PageTitle({ title }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, md: 1.5 } }}>
      <Box
        sx={{
          width: 5,
          height: { xs: 28, md: 34 },
          borderRadius: 999,
          flexShrink: 0,
          background: 'linear-gradient(180deg, #3ab5b0, #2a938f)',
          boxShadow: '0 2px 10px rgba(42,147,143,0.28)',
        }}
      />
      <Box>
        <Typography
          variant="h4"
          fontWeight={800}
          sx={{
            color: 'primary.main',
            fontSize: { xs: '1.45rem', md: '1.75rem' },
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            mt: 0.75,
            width: { xs: 40, md: 52 },
            height: 3,
            borderRadius: 999,
            background: 'linear-gradient(90deg, #3ab5b0, #2a938f)',
            opacity: 0.85,
          }}
        />
      </Box>
    </Box>
  );
}

function EmptyState({ icon: Icon, title, message }) {
  return (
    <Paper
      {...pagePaperSx}
      sx={{ ...pagePaperSx.sx, py: { xs: 6, md: 8 }, px: 3, textAlign: 'center' }}
    >
      <Box
        sx={{
          width: 88,
          height: 88,
          borderRadius: '50%',
          bgcolor: alpha('#2a938f', 0.08),
          border: 1,
          borderColor: alpha('#2a938f', 0.14),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2.5,
          boxShadow: '0 8px 24px rgba(42,147,143,0.12)',
        }}
      >
        <Icon sx={{ fontSize: 44, color: 'primary.main' }} />
      </Box>
      {title ? (
        <Typography variant="h6" fontWeight={800} color="primary.main" gutterBottom>
          {title}
        </Typography>
      ) : null}
      <Typography color="text.secondary" fontWeight={600} sx={{ maxWidth: 400, mx: 'auto' }}>
        {message}
      </Typography>
    </Paper>
  );
}

export default function Search() {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const rawItems = [];
        let page = 1;
        let lastPage = 1;
        do {
          const response = await apiFetch(withServiceQuery(`/users/products?page=${page}`));
          if (!response.ok) break;
          const json = await response.json();
          rawItems.push(...(json.data ?? []).filter(isAppServiceItem));
          lastPage = json.meta?.last_page ?? 1;
          page += 1;
        } while (page <= lastPage);
        if (!cancelled) {
          setAllProducts(sortApiProducts(rawItems).map(mapApiProduct));
        }
      } catch {
        if (!cancelled) setAllProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [i18n.language]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return allProducts.filter((p) => p.name.toLowerCase().includes(q));
  }, [allProducts, query]);

  const hasQuery = query.trim() !== '';

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '70vh', pb: { xs: 4, md: 6 } }}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          py: { xs: 2.5, md: 3 },
          mb: { xs: 3, md: 4 },
        }}
      >
        <Container maxWidth="lg">
          <PageTitle title={t('search')} />

          <TextField
            fullWidth
            placeholder={t('search_placeholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
            sx={{
              mt: 2.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                bgcolor: '#F4FAF9',
                transition: 'box-shadow 0.2s ease',
                '&.Mui-focused': {
                  boxShadow: `0 0 0 4px ${alpha('#2a938f', 0.12)}`,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
            }}
          />
        </Container>
      </Box>

      <Container maxWidth="lg">
        {hasQuery && !loading && results.length > 0 && (
          <Typography color="text.secondary" fontWeight={600} sx={{ mb: 2 }}>
            {results.length} {t('products').toLowerCase()}
          </Typography>
        )}

        {loading ? (
          <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
            {Array.from({ length: 10 }).map((_, i) => (
              <Grid key={i} size={{ xs: 6, sm: 4, md: 3, lg: 2.4 }}>
                <Skeleton variant="rounded" height={260} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        ) : !hasQuery ? (
          <EmptyState icon={SearchIcon} message={t('search_hint')} />
        ) : results.length === 0 ? (
          <EmptyState
            icon={SearchOffIcon}
            title={t('search')}
            message={t('search_no_results', { query })}
          />
        ) : (
          <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
            {results.map((product) => (
              <Grid key={product.id} size={{ xs: 6, sm: 4, md: 3, lg: 2.4 }}>
                <ProductGridItem
                  item={product}
                  addToCartLabel={t('add_to_cart')}
                  favouriteLabel={t('favourite_a11y')}
                  discountOffLabel={t('discount_off')}
                  unavailableLabel={t('product_unavailable')}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
}
